import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronIcon } from "../../../Core/svgV2/Chevron";
import CustomSearchBox from "../../../Core/CommonV2/CustomSearchBox";
import { useAppDispatch, useAppSelector } from "../../../Core/redux/hooks";
import {
  resetVault,
  updateVault,
} from "../../../Core/redux/slices/vault.slice";
import NewButton from "./NewButton";
import { Breadcrumb, Popover, Tooltip } from "antd";
import usePermission from "../../../Core/hooks/usePermission";
import {
  CrossIcon,
  CrossIcon2,
  CrossIconLarge,
} from "../../../Core/svgV2/CrossIcon";
import { SearchElongatedIcon } from "../../../Core/svgV2/SearchIcon";
import ViewToggleButton from "./ViewToggleButton";
import { client } from "../../../Core/utils/axiosClient";
import { FilterIcon } from "../../../Core/svgV2/FilterIcon";
import LibraryFilters, { AppliedLibraryFilters } from "../Filters";

const ProjectDocsMenuBar = ({ viewType, setViewType, onUploadClick }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  const [enhancedBreadcrumbs, setEnhancedBreadcrumbs] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { projectList } = useAppSelector((state) => state.projects);
  const { currentSelectedProject, search } = useAppSelector(
    (state) => state.vault
  );
  const { projectId, "*": splat } = useParams();

  const pathSnippets = splat?.split("/").filter((i) => i);

  useEffect(() => {
    const currentProject = projectList?.find((p) => p.id == projectId);
    if (currentProject?.title) {
      setProjectName(currentProject.title);
      // then update the currentSelectedProject in the redux store
      dispatch(
        updateVault({
          key: "currentSelectedProject",
          value: currentProject,
        })
      );
    } else {
      setProjectName(`Project ${projectId}`);
    }
  }, [projectId, currentSelectedProject, projectList]);

  const { "*": spalt } = useParams();

  const { hasPermission: FOLDER_FILE_LINK_UPLOAD } = usePermission("22");
  const { currentSelectedFolder } = useAppSelector((state) => state.vault);

  let hasPermission = FOLDER_FILE_LINK_UPLOAD;

  if (spalt?.length) {
    hasPermission =
      currentSelectedFolder?.canEdit === 1 ||
      currentSelectedFolder?.isOwner === 1;
  }

  const getCurrentFolderName = () => {
    if (!splat) return null;

    const pathSnippets = splat.split("/").filter((i) => i);
    if (pathSnippets.length === 0) return null;

    const lastSegment = pathSnippets[pathSnippets.length - 1];
    const parts = lastSegment.split("-");
    const folderName = parts.slice(0, -1).join("-");

    return folderName || null;
  };

  // build breadcrumbs by parsing the URL path
  const buildBreadcrumbsFromPath = async () => {
    if (!pathSnippets || pathSnippets.length === 0) return [];

    const breadcrumbItems = [];
    let currentPath = "";

    for (let i = 0; i < pathSnippets.length; i++) {
      const segment = pathSnippets[i];
      const parts = segment.split("-");
      const name = parts.slice(0, -1).join("-");
      const id = parts[parts.length - 1];

      currentPath += (i === 0 ? "" : "/") + segment;

      if (id && name) {
        breadcrumbItems.push({
          id,
          folderName: name,
          path: currentPath,
        });
      }
    }

    return breadcrumbItems;
  };

  const handleBackClick = () => {
    if (!splat) {
      // If we're at project root, go back to library
      navigate("/library");
      return;
    }

    const pathSnippets = splat.split("/").filter((i) => i);

    if (pathSnippets.length === 1) {
      // iif we r one level deep go back to project root
      navigate(`/library/${projectId}`);
    } else {
      // go back one folder level
      const parentPath = pathSnippets.slice(0, -1).join("/");
      navigate(`/library/${projectId}/${parentPath}`);
    }
  };

  const currentFolderName = getCurrentFolderName();

  useEffect(() => {
    const fetchBreadcrumbs = async () => {
      const pathBreadcrumbs = await buildBreadcrumbsFromPath();
      setEnhancedBreadcrumbs(pathBreadcrumbs);
    };

    fetchBreadcrumbs();
  }, [location.pathname]);

  return (
    <div className="flex w-full py-2 px-6 bg-white border-b border-gray-200 items-center justify-between">
      <div className="flex items-center gap-x-3">
        <button
          className="flex items-center gap-x-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
          onClick={handleBackClick}
        >
          <ChevronIcon className="text-gray-700 rotate-90" />
          <span className="text-black out-500-14">
            {currentFolderName
              ? currentFolderName
              : currentSelectedProject.title}
          </span>
        </button>
      </div>

      <div className="flex items-center gap-x-3">
        <div className="flex items-center gap-x-3 relative">
          {isSearchOpen && (
            <div className="absolute right-0 z-10 bg-white">
              <CustomSearchBox
                suffix={
                  <button
                    onClick={() => {
                      setIsSearchOpen(false);
                      dispatch(updateVault({ key: "search", value: "" }));
                    }}
                    className="hover:bg-gray-100 rounded p-0 transition-colors focus:outline-none focus:ring-0 active:bg-gray-200"
                  >
                    <CrossIcon2 width={16} height={16} />
                  </button>
                }
                placeholder={`Search in ${
                  currentFolderName || projectName || "Library"
                }`}
                className="w-[285px] h-[30px] border-[#7DC7FF] focus-within:border-[#7DC7FF] focus-within:shadow-sm transition-all"
                search={search}
                setSearch={(s) =>
                  dispatch(updateVault({ key: "search", value: s }))
                }
              />
            </div>
          )}

          <button
            className={`${isSearchOpen ? "hidden" : "block"}`}
            onClick={() => setIsSearchOpen(!isSearchOpen)}
          >
            <SearchElongatedIcon className="text-gray-500" />
          </button>
        </div>

        {projectId && (
          <div className="flex items-center">
            <AppliedLibraryFilters />
            <Popover
              trigger={["click"]}
              content={
                <LibraryFilters
                  onClose={() => {
                    setFiltersOpen(false);
                  }}
                />
              }
              arrow={false}
              open={filtersOpen}
              placement="bottomLeft"
              onOpenChange={(val) => {
                console.log("yo Popover open state changed:", val);
                setFiltersOpen(val);
              }}
            >
              <button
                className={`flex items-center gap-x-2 px-[10px] py-1.5 h-[30px] bg-white rounded hover:bg-gray-50  ${
                  filtersOpen && "bg-gray-50"
                }`}
              >
                <FilterIcon className="text-gray-500" />
                <p className="out-500-14 leading-5 text-gray-500">Filter</p>
              </button>
            </Popover>
          </div>
        )}

        <ViewToggleButton viewType={viewType} setViewType={setViewType} />

        <NewButton onUploadClick={onUploadClick} />
      </div>
    </div>
  );
};

export default ProjectDocsMenuBar;
